// src/components/ProductTour.tsx
import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

interface TourStep {
  target: string; // CSS selector for the element to highlight
  title: string;
  description: string;
  position: 'left' | 'right' | 'top' | 'bottom';
}

const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="home"]',
    title: 'Dashboard Home',
    description: 'Your performance overview with weekly macros, weight tracking, and training summary all in one place.',
    position: 'right'
  },
  {
    target: '[data-tour="nutrition"]',
    title: 'Nutrition Tracking',
    description: 'Log your daily meals, track macros in real-time, and see your calorie breakdown.',
    position: 'right'
  },
  {
    target: '[data-tour="nutrition-plan"]',
    title: 'AI Nutrition Plans',
    description: 'Generate personalized meal plans powered by AI. Tailored to your macros, preferences, and goals.',
    position: 'right'
  },
  {
    target: '[data-tour="training"]',
    title: 'Training Log',
    description: 'View all your workouts synced from Garmin and WHOOP, including heart rate, calories, and performance metrics.',
    position: 'right'
  },
  {
    target: '[data-tour="training-program"]',
    title: 'Training Programs',
    description: 'AI-generated training programs based on your sport and goals. Automatically syncs to your Garmin device.',
    position: 'right'
  },
  {
    target: '[data-tour="settings"]',
    title: 'Settings',
    description: 'Connect wearables, manage your subscription, update preferences, and customize your experience.',
    position: 'right'
  }
];

interface ProductTourProps {
  onComplete: () => void;
  onSkip: () => void;
}

export default function ProductTour({ onComplete, onSkip }: ProductTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [highlightPosition, setHighlightPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  // Calculate positions for spotlight and tooltip
  useEffect(() => {
    const targetElement = document.querySelector(step.target);
    if (!targetElement) return;

    const rect = targetElement.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;
    
    // Highlight position (with padding)
    setHighlightPosition({
      top: rect.top - 8,
      left: rect.left - 8,
      width: rect.width + 16,
      height: rect.height + 16
    });

    // Tooltip position based on step.position
    let tooltipTop = 0;
    let tooltipLeft = 0;

    if (isMobile) {
      // On mobile, always position at bottom center
      tooltipTop = window.innerHeight - 280; // Fixed to bottom
      tooltipLeft = 16; // 16px from left edge (with padding)
    } else {
      // Desktop positioning
      switch (step.position) {
        case 'right':
          tooltipTop = rect.top;
          tooltipLeft = rect.right + 24;
          break;
        case 'left':
          tooltipTop = rect.top;
          tooltipLeft = rect.left - 400; // tooltip width is ~360px
          break;
        case 'top':
          tooltipTop = rect.top - 200;
          tooltipLeft = rect.left;
          break;
        case 'bottom':
          tooltipTop = rect.bottom + 24;
          tooltipLeft = rect.left;
          break;
      }
    }

    setTooltipPosition({ top: tooltipTop, left: tooltipLeft });
  }, [currentStep, step]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      {/* Overlay with cutout for spotlight */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={highlightPosition.left}
              y={highlightPosition.top}
              width={highlightPosition.width}
              height={highlightPosition.height}
              rx="12"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(15,23,42,0.12)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Spotlight border */}
      <div
        className="absolute border-4 border-green-400 rounded-xl pointer-events-none transition-all duration-500 ease-out"
        style={{
          top: `${highlightPosition.top}px`,
          left: `${highlightPosition.left}px`,
          width: `${highlightPosition.width}px`,
          height: `${highlightPosition.height}px`,
        }}
      >
        {/* Animated border pulse */}
        <div className="absolute inset-0 border-4 border-green-400 rounded-xl animate-pulse opacity-50" />
      </div>

      {/* Tooltip */}
      <div
        className="absolute w-[calc(100vw-2rem)] md:w-96 max-w-md bg-gradient-to-br from-gray-900 to-black border-2 border-green-400/50 rounded-2xl p-4 md:p-6 shadow-2xl transition-all duration-500 ease-out pointer-events-auto"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
        }}
      >
        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 text-gray-500 hover:text-[#0f172a] transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold">
              {currentStep + 1}
            </div>
            <h3 className="text-xl font-bold text-[#0f172a]">{step.title}</h3>
          </div>
          <p className="text-gray-600 leading-relaxed">{step.description}</p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-2 mb-6">
          {TOUR_STEPS.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStep
                  ? 'w-8 bg-green-400'
                  : index < currentStep
                  ? 'w-2 bg-green-400/50'
                  : 'w-2 bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={onSkip}
            className="text-xs md:text-sm text-gray-500 hover:text-[#0f172a] transition font-medium px-2 py-2"
          >
            Skip tour
          </button>

          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="px-3 md:px-4 py-2.5 md:py-2 bg-gray-800 hover:bg-gray-700 text-[#0f172a] rounded-xl transition flex items-center gap-2 text-sm md:text-base"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-4 md:px-6 py-2.5 md:py-2 bg-green-500 hover:bg-green-400 text-[#0f172a] font-semibold rounded-xl transition flex items-center gap-2 text-sm md:text-base"
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Step counter */}
        <div className="mt-4 text-center">
          <span className="text-xs text-gray-500">
            Step {currentStep + 1} of {TOUR_STEPS.length}
          </span>
        </div>
      </div>
    </div>
  );
}
